import React from "react";

import { useTranslation } from "@refinedev/core";

import { FormControl, InputLabel, OutlinedInput, Select, useMediaQuery, useTheme } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";

import dayjs from "dayjs";

import { DAYJS_MAP, INTERVALS, Interval, MOBILE_INTERVAL_RANGES, WEB_INTERVAL_RANGES } from "./chart-types";

interface DateRangeSelectProps {
  interval: Interval;
  range: number;
  maxDate: Date;
  onDateChange: (intervalNum: number, minDate: Date, maxDate: Date, range?: number) => void;
  onCustomClick: () => void;
}

export function DateRangeSelect({ interval, range, maxDate, onDateChange, onCustomClick }: DateRangeSelectProps) {
  const { translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const value = `${interval}-${range || 1}`;

  const getFormattedMessage = (currentInterval: Interval, count: number) => {
    const unitMap: Record<string, string> = {
      [Interval.Hour]: translate("trends.controls.hours"),
      [Interval.Day]: translate("trends.controls.days"),
      [Interval.Week]: translate("trends.controls.weeks"),
      [Interval.Month]: translate("trends.controls.months"),
      [Interval.Year]: translate("trends.controls.years"),
    };

    const unit = unitMap[currentInterval];
    if (!unit) {
      return undefined;
    }

    if (count === 1) {
      return translate("trends.controls.lastSingular", { unit: unit.toLowerCase() });
    }
    return translate("trends.controls.last", { count, unit: unit.toLowerCase() });
  };

  const options = Array.from(isMobile ? MOBILE_INTERVAL_RANGES.entries() : WEB_INTERVAL_RANGES.entries()).reduce<
    React.JSX.Element[]
  >((acc, curr) => {
    const [intervalKey, rangeOptions] = curr;
    for (const val of rangeOptions) {
      const text = getFormattedMessage(intervalKey, val);
      acc.push(
        <option key={`metric-date-${intervalKey}-${val}`} value={`${intervalKey}-${val}`}>
          {intervalKey === Interval.Custom ? translate("trends.controls.custom") : text}
        </option>
      );
    }
    return acc;
  }, []);

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { value } = event.target;
    const [newInterval, newRange] = value.split("-");

    const intervalVal = newInterval as Interval;
    const rangeVal = Number.isNaN(Number(newRange)) ? null : parseInt(newRange);

    if (intervalVal === Interval.Custom) {
      onCustomClick();
      return;
    }
    if (!rangeVal) {
      return;
    }
    const max = maxDate;
    const min = dayjs(maxDate).subtract(rangeVal, DAYJS_MAP[intervalVal]).toDate();
    onDateChange(INTERVALS[intervalVal], min, max, rangeVal);
  };

  return (
    <FormControl size="small">
      <InputLabel shrink sx={{ color: "text.secondary" }}>
        {translate(isMobile ? "trends.controls.selectDate" : "trends.controls.moreDates")}
      </InputLabel>
      <Select
        autoWidth
        native
        onChange={handleSelectChange}
        input={
          <OutlinedInput
            size="small"
            sx={{
              minHeight: 30,
              boxShadow: theme => theme.shadows[1],
              height: 35,
              fontSize: "14px",
            }}
          />
        }
        value={value}
      >
        <option value="">{translate("trends.controls.select")}</option>
        {options}
      </Select>
    </FormControl>
  );
}
